import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Container,
  Divider,
  CardActionArea,
  Stack,
  Paper,
  Link,
  Grid2,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { TipsAndUpdates } from "@mui/icons-material";
import { ReactComponent as SASTPhase } from "../../../images/SASTPhase.svg";
import { ReactComponent as DASTPhase } from "../../../images/DASTPhase.svg";
import { ReactComponent as SecurityTestPlan } from "../../../images/SecurityTestPlan.svg";

function OptionCard({ icon, title, description, onClick }) {
  return (
    <Card
      elevation={0}
      sx={{
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          transform: "translateY(-4px)",
        },
      }}
      onClick={onClick}
    >
      <CardActionArea
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          padding: 0,
        }}
      >
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: {
              xs: 2,
              sm: 2.5,
              md: 3,
            },
          }}
        >
          {/* Icon and Title - Fixed Space */}
          <Box
            sx={{
              width: "100%",
              mb: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent="center"
              sx={{ width: "100%" }}
            >
              <Box
                sx={{
                  fontSize: {
                    xs: "1.4rem",
                    sm: "1.6rem",
                    md: "1.8rem",
                  },
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1rem",
                    md: "1.1rem",
                  },
                  fontWeight: 600,
                  textAlign: "left",
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
            </Stack>
          </Box>

          {/* Description - Flexible Space */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "flex-start",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: {
                  xs: "0.75rem",
                  sm: "0.8rem",
                  md: "0.85rem",
                },
                lineHeight: 1.4,
                textAlign: "center",
                width: "100%",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: {
                  xs: 8, 
                  sm: 10,
                  md: 12,
                },
                WebkitBoxOrient: "vertical",
                wordWrap: "break-word",
              }}
            >
              {description}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

const SecurityTestingHomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleNavigate = (index) => () => {
    if (index === 0) {
      navigate("/dashboard/security-testing/sast-test-plan-input-page");
    } 
    else if (index === 1) {
      navigate("/dashboard/security-testing/dast-test-plan-input-page")
    }
    else if (index == 2) {
      navigate("/dashboard/security-testing/comprehensive-test-plan-input-page")
    }
  };

  const phases = [
    {
      id: "sast-test-plan",
      title: "SAST Test Plan",
      description:
        "Defines the strategy and scope for analyzing source code to detect security flaws, insecure coding practices, and compliance issues before deployment.",
      icon: <SASTPhase />,
      bordered: false,
    },
    {
      id: "dast-test-plan",
      title: "DAST Test Plan",
      description:
        "Outlines the approach for dynamically testing live applications to identify exploitable vulnerabilities, configuration flaws, and runtime security risks.",
      icon: <DASTPhase />,
      bordered: false,
    },
    {
      id: "comprehensive -test-plan",
      title: "Comprehensive Test Plan",
      description:
        "Defines both static and dynamic testing strategies to ensure end-to-end security coverage—protecting the webapplication’s code, configuration, and runtime environment from threats.",
      icon: <SecurityTestPlan />,
      bordered: false,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: {
          xs: 4,
          sm: 5,
          md: 6,
        },
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
      }}
    >
      {/* Title */}
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontSize: {
            xs: "1.8rem",
            sm: "2.125rem",
            md: "2.5rem",
          },
        }}
      >
        Security Test Plan
      </Typography>
      <Box>
        <Typography
          variant="subtitle1"
          color="text"
          textAlign="center"
          sx={{
            maxWidth: {
              xs: "95%",
              sm: "90%",
              md: "85%",
              lg: "80%",
            },
            mx: "auto",
            fontSize: {
              xs: "0.9rem",
              sm: "1rem",
              md: "1.1rem",
            },
          }}
        >
          Transform your security testing process with intelligent automation. Detect vulnerabilities early, analyze threats, and ensure robust protection across every layer of your application.
        </Typography>
      </Box>

      <Divider
        sx={{
          my: {
            xs: 3,
            sm: 4,
            md: 5,
          },
          width: "100%",
          maxWidth: {
            xs: "100%",
            sm: "800px",
            md: "900px",
          },
          borderBottomWidth: 2,
        }}
      />

      {/* Phases Grid - Using proper Grid2 */}
      <Container
        maxWidth="lg"
        sx={{
          px: {
            xs: 1,
            sm: 2,
            md: 3,
          },
        }}
      >
        <Grid2
          container
          spacing={{
            xs: 2,
            sm: 3,
            md: 4,
          }}
          sx={{
            justifyContent: "center",
            alignItems: "stretch", // This ensures equal heights
          }}
        >
          {phases.map((phase, index) => (
            <Grid2
              key={phase.id}
              size={{ xs: 12, sm: 6 }}
              sx={{
                display: "flex",
                minHeight: 0, // Allows flex children to shrink
              }}
            >
              <OptionCard
                icon={phase.icon}
                title={phase.title}
                description={phase.description}
                onClick={handleNavigate(index)}
              />
            </Grid2>
          ))}
        </Grid2>
      </Container>

      {/* Tip Section */}
      <Paper
        elevation={0}
        sx={{
          mt: {
            xs: 6,
            sm: 7,
            md: 8,
          },
          bgcolor: "#fffbea",
          border: "1px solid #ffe58f",
          borderRadius: 2,
          p: {
            xs: 2,
            sm: 2.5,
            md: 3,
          },
          mx: 2,
          maxWidth: {
            xs: "100%",
            sm: "90%",
            md: "800px",
            lg: "900px",
          },
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <TipsAndUpdates sx={{ color: "#faad14", flexShrink: 0 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: {
              xs: "0.8rem",
              sm: "0.875rem",
            },
          }}
        >
          <b>Here is a tip for you:</b> Most teams start with the Requirement
          Phase for comprehensive security testing. You can navigate between
          phases as needed.
        </Typography>
      </Paper>

      {/* Help Link */}
      <Link
        // href="#"
        underline="hover"
        sx={{
          mt: 3,
          fontSize: {
            xs: "0.8rem",
            sm: "0.9rem",
          },
          color: "primary.main",
        }}
      >
        Need Help?
      </Link>
    </Box>
  );
};

export default SecurityTestingHomePage;

